/** @jsxRuntime classic  */
/** @jsx jsx */
/** @jsxfrag */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { css, jsx } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouterActions } from 'react-resource-router';

import { Grid } from '@atlaskit/page';
import { Box, xcss } from '@atlaskit/primitives';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { useTabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { getPermalink } from '@atlassian/bitkit-diff/exports/diff-permalink';

import { CodeReviewerFeedback } from 'src/components/code-reviewer-feedback/src/components/code-reviewer-feedback';
import { commentHasCodeSuggestions } from 'src/components/code-reviewer-feedback/src/util';
import { SendAnalyticsEvent } from 'src/components/conversation/src/components/types';
import {
  DEFAULT_CODE_REVIEWER_COMMENT_UUIDS,
  identifyCodeReviewerComment,
} from 'src/components/conversation/src/utils/code-reviewer-comment-identifiers';
import {
  ApiComment,
  FabricComment,
} from 'src/components/conversation-provider/types';
import { toFabricUser } from 'src/components/conversation-provider/utils';
import { DiffContext } from 'src/components/diff-set/diff-provider';
import { useFetchConflicts } from 'src/components/diff-set/hooks/conflicts';
import { useUntruncatedDiffCounts } from 'src/components/diff-set/hooks/untruncated-diff-counts';
import { EditorSource } from 'src/components/editor/with-analytics';
import {
  CommentCreateEvent,
  CommentExpandCollapseEvent,
  ConversationsContextProvider,
  NewConversationClickEvent,
  buildConversationContext,
  RenderAnalyticsAttributeAction,
} from 'src/contexts/conversations-context';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useConfigString } from 'src/contexts/statsig-config-provider';
import { useFlag } from 'src/hooks/flag';
import { useIntl } from 'src/hooks/intl';
import { MetricSender, PULL_REQUEST_LOAD } from 'src/metrics';
import updateMobileHeaderState from 'src/redux/global/actions/update-mobile-header-state';
import {
  ADD_COMMENT,
  DELETE_COMMENT,
  PULL_REQUEST_TAB_REDIRECT,
  onPermalinkHashChange,
} from 'src/redux/pull-request/actions';
import { fetchPendingMergeStatus } from 'src/redux/pull-request/merge-reducer';
import {
  getCurrentPullRequest,
  getHighlightCommentThread,
  getHighlighted,
  getPullRequestDestinationHash,
  getPullRequestDestinationRepoFullSlug,
  getPullRequestSourceHash,
} from 'src/redux/pull-request/selectors';
import commentUrls from 'src/redux/pull-request/urls';
import { useParams } from 'src/router/hooks';
import {
  AUTOREVIEW_DOGFOODING_FLAG_ID,
  AUTOREVIEW_DOGFOODING_FLAG_STORE_KEY,
} from 'src/sections/global/components/flags/autoreview-dogfooding/autoreview-dogfooding-flag';
import { usePrCommitCount } from 'src/sections/repository/sections/pull-request/hooks/commit-list/pull-request-commit-list';
import { getConversationsMentionsContext } from 'src/selectors/conversation-selectors';
import { getCurrentRepositoryUuid } from 'src/selectors/repository-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { getWorkspacePlan } from 'src/selectors/workspace-selectors';
import urls from 'src/urls';
import { publishTrackEvent, publishUiEvent } from 'src/utils/analytics/publish';
import store from 'src/utils/store';

import { PullRequestDialogZone } from '../../containers/pull-request-dialog-zone';
import { PendingReviewWarningCheck } from '../../pending-review-warning-check';
import { DiffFeatures, DiffFeaturesContext } from '../diff-features-context';
import { getLastReviewedCommit } from '../iterative-review/utils';
import { SyntaxHighlightingFlagWrapper } from '../syntax-highlighting-flag-wrapper';

import { CommitTab } from './commit-tab';
import { TabsContext } from './context/tabs-context';
import {
  DiffStateContext,
  IterativeReviewSelectedDiffState,
  MultiCommitSelection,
  SelectedDiffType,
  defaultMultiCommitSelection,
} from './diff/diff-state-context';
import { DiffTab } from './diff/diff-tab';
import { PullRequestDiffProvider } from './diff/pull-request-diff-provider';
import { Header } from './header';
import { useSelectedTab } from './hooks/use-selected-tab';
import { useTabRedirect } from './hooks/use-tab-redirect';
import { PROverviewTab } from './pr-overview-tab';
import { PRTabsContainer } from './pr-tabs-container';
import { PullRequestKeyboardShortcuts } from './shortcuts/pull-request-keyboard-shortcuts';

export const PULL_REQUEST_TABS = ['overview', 'diff', 'commits'] as const;
export type TabSelectedType = (typeof PULL_REQUEST_TABS)[number];

const panelStyles = css({
  '&&': {
    paddingTop: token('space.200'),
  },
});

const tabsWrapper = xcss({
  marginInline: 'space.250',
  width: '100%',
  // 40px is the page margin + padding of the tabs container
  maxWidth: 'calc(100% - 40px)',
});

const CustomTabPanel = ({ children }: { children: any }) => {
  const tabPanelAttributes = useTabPanel();

  /* eslint react/no-unknown-property: "warn" */
  return (
    <div css={panelStyles} {...tabPanelAttributes} tabIndex={-1}>
      {children}
    </div>
  );
};

const diffFeaturesContext: DiffFeatures = {
  multiCommentReview: true,
  hasMarkFilesAsViewed: true,
  hasAnnotations: true,
  hasComments: true,
  previewMergeOption: true,
  extraStickyHeaderHeight: true,
  hasCommentNav: true,
  hasCodeSuggestions: true,
};

const PreLoadDiffStat = () => {
  const { loadDiffStat } = useContext(DiffContext);
  useEffect(() => {
    loadDiffStat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

export const PullRequestDetailsTabbed = () => {
  const { replace } = useRouterActions();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { repositoryOwner, repositorySlug, pullRequestId } = useParams<{
    repositoryOwner: string;
    repositorySlug: string;
    pullRequestId: string;
  }>();

  const autoreviewDogfoodingFlagEnabled = useStatsigGate(
    StatsigFeatureKeys.autoreviewDogfoodingFlag
  );
  const autoreviewDogfoodingFlagViewed = store.get(
    AUTOREVIEW_DOGFOODING_FLAG_STORE_KEY
  );
  const { showFlagComponent } = useFlag();

  const destRepoFullSlug =
    useSelector(getPullRequestDestinationRepoFullSlug) || '';
  const destHash = useSelector(getPullRequestDestinationHash) || '';
  const anchorHash = useSelector(getPullRequestSourceHash) || '';
  const currentUser = useSelector(getCurrentUser);
  const fabricUser = toFabricUser(currentUser);
  const repoId = useSelector(getCurrentRepositoryUuid) || undefined;
  const { isWorkspacePaid } = useSelector(getWorkspacePlan);
  const conversationsMentionContext = useSelector(
    getConversationsMentionsContext
  );
  const pullRequest = useSelector(getCurrentPullRequest);
  const { untruncatedDiffFileCount: fileCount } = useUntruncatedDiffCounts();
  const commitCount = usePrCommitCount();
  const handleHashChange = () => {
    const permalink = getPermalink();
    if (permalink) {
      dispatch(updateMobileHeaderState('none'));
      dispatch(onPermalinkHashChange(permalink));
    }
  };

  useEffect(() => {
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This is a reference that will be used to provide a container for CSS
  // variables that should apply to the scope of the PR page. The ref can be
  // passed to any component that would like to provide a variable.
  //
  // Previously we were storing component heights in React state. When those
  // heights changed, there was a lot of recalculating and rerendering,
  // leading to very slow updates. Putting these values in CSS allows the
  // browser to render quickly without us interfering with it.
  const cssVarContainerRef = React.useRef<HTMLDivElement>(null);

  const initialTab = useSelectedTab();
  const [tabSelected, setTabSelected] = useState<TabSelectedType>(initialTab);

  const { redirectLink, newTab } = useTabRedirect(tabSelected) || {};
  if (redirectLink && newTab) {
    setTabSelected(newTab);
    replace(redirectLink);
    dispatch({ type: PULL_REQUEST_TAB_REDIRECT });
  }

  const trackOverviewClick = usePressTracing('pull-request-overview');
  const trackDiffClick = usePressTracing('pull-request-diff');
  const trackCommitsClick = usePressTracing('pull-request-commits');

  const onSelectTab = (index: number) => {
    // since tabs can be switched from sticky header in the middle of a page,
    // make sure to scroll to the top of the page when switching tabs
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    switch (index) {
      case 0:
        trackOverviewClick();
        break;
      case 1:
        trackDiffClick();
        break;
      case 2:
        trackCommitsClick();
        break;
    }

    const newTabSelected = PULL_REQUEST_TABS[index];
    setTabSelected(newTabSelected);
  };

  const switchTabShortcutHandler = (tab: TabSelectedType) => {
    // if the tab is already selected, don't do anything
    if (tab === tabSelected) {
      return;
    }

    replace(
      urls.ui.pullRequestTab(
        repositoryOwner,
        repositorySlug,
        pullRequestId,
        tab
      )
    );
    setTabSelected(tab);
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const { commentSave, commentUpdate, commentDelete, commentResolve } =
    commentUrls.api.v20;

  const onNewConversationClick = (event: NewConversationClickEvent) => {
    const actionSubjectId =
      event.lineFrom || event.lineTo
        ? 'addDiffCommentButton'
        : 'addFileCommentButton';
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId,
      source: 'prView',
      attributes: {
        file: event.file,
        activityFeedVersion: 'new',
      },
    });
  };

  const onCommentCreate = useCallback(
    (event: CommentCreateEvent) => {
      publishTrackEvent({
        action: 'created',
        actionSubject: 'pullRequestComment',
        actionSubjectId: event.commentId,
        source: 'pullRequestScreen',
        attributes: {
          role: event.role,
          pullRequestLocalId: event.pullRequestLocalId,
          repoUuid: event.repoUuid,
          pullRequestStatus: event.pullRequestStatus,
          pending: !!event.pending,
          selectedTab: tabSelected,
        },
      });
    },
    [tabSelected]
  );

  const onCommentExpandCollapse = (event: CommentExpandCollapseEvent) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'collapseExpandButton',
      actionSubjectId: event.action,
      source: 'prView',
      attributes: {
        resolved: event.resolved,
        prStatus: event.prStatus,
        prId: event.prId,
        paid: event.paid,
        repoId: event.repoId,
        role: event.role,
        activityFeedVersion: 'new',
      },
    });
  };

  // We have to leave the diff selection things for iterative review here so that
  // the conversation context is updated correctly.
  const [iterativeReviewSelectedDiff, setIterativeReviewSelectedDiff] =
    useState<IterativeReviewSelectedDiffState>({
      sourceHash: '',
      isLoading: true,
      selectedDiffType: SelectedDiffType.None,
    });

  // used to track mouse location as the end of commit range when selecting a diff
  // in the iterative review selector
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [multiCommitSelection, setMultiCommitSelection] =
    useState<MultiCommitSelection>(defaultMultiCommitSelection);

  // Don't set the diff type until we have the hash (if available) to keep the dropdown
  // from rendering one label then a different one
  const [commitCSLRHash, setCommitCSLRHash] = useState<string>('');
  // fetch the last reviewed commit from local storage or the PR service
  useEffect(() => {
    const fetchAndSetCommit = async () => {
      const cslrHash = await getLastReviewedCommit(
        repositoryOwner,
        repositorySlug,
        pullRequestId
      );
      setCommitCSLRHash(cslrHash);
    };

    fetchAndSetCommit();
  }, [repositoryOwner, repositorySlug, pullRequestId, setCommitCSLRHash]);

  const highlightedCommentId = useSelector(getHighlighted) || undefined;
  const highlightCommentThread =
    useSelector(getHighlightCommentThread) || false;

  const diffAnchor = iterativeReviewSelectedDiff.sourceHash ?? undefined;

  const diffDestRev = iterativeReviewSelectedDiff.destHash ?? undefined;

  const isCodeReviewerFeedbackStatsigGateEnabled = useStatsigGate(
    StatsigFeatureKeys.enableCodeReviewerFeedbackUi
  ) as boolean;

  const commentUUIDs = useConfigString(
    StatsigFeatureKeys.codeReviewerCommentUuidConfig,
    DEFAULT_CODE_REVIEWER_COMMENT_UUIDS
  );

  const conversationContext = useMemo(() => {
    const newUrls = {
      commentSave: () =>
        commentSave(repositoryOwner, repositorySlug, pullRequestId),
      commentUpdate: (commentId: number | string) =>
        commentUpdate(
          repositoryOwner,
          repositorySlug,
          pullRequestId,
          commentId
        ),
      commentDelete: (commentId: number | string) =>
        commentDelete(
          repositoryOwner,
          repositorySlug,
          pullRequestId,
          commentId
        ),
      commentResolve: (commentId: number | string) =>
        commentResolve(
          repositoryOwner,
          repositorySlug,
          pullRequestId,
          commentId
        ),
    };

    const eventHandlers = {
      onNewConversationClick,
      onCommentCreate,
      onCommentExpandCollapse,
    };

    const onAddComment = (payload: ApiComment) =>
      dispatch({
        type: ADD_COMMENT.SUCCESS,
        payload: {
          comment: payload,
        },
      });

    const onDeleteComment = (payload: { id: string }) =>
      dispatch({
        type: DELETE_COMMENT.SUCCESS,
        payload,
      });

    const renderBetweenCommentAndActions = (
      comment: FabricComment,
      sendAnalyticsEvent: SendAnalyticsEvent,
      disableRender?: boolean,
      collapseComment?: () => void
    ) => {
      const props = {
        comment,
        sendAnalyticsEvent,
        pullRequest,
        collapseComment,
      };

      return pullRequest ? (
        <CodeReviewerFeedback {...props} isFileLevelComment={!disableRender} />
      ) : (
        <></>
      );
    };

    const additionalAnalyticsAttributes = (
      comment: FabricComment,
      action: RenderAnalyticsAttributeAction
    ) => {
      const sentiment = (): {} => {
        switch (action) {
          case 'onDelete':
            return { codeReviewerFeedbackSentiment: 'negative' };
          case 'onReply':
            return { codeReviewerFeedbackSentiment: 'neutral' };
          case 'onResolve':
            return { codeReviewerFeedbackSentiment: 'neutral' };
          case 'onReopen':
            return { codeReviewerFeedbackSentiment: 'positive' };
          default:
            return {};
        }
      };

      return isCodeReviewerFeedbackStatsigGateEnabled &&
        identifyCodeReviewerComment(comment.createdBy, commentUUIDs)
        ? {
            commentId: comment.commentId,
            prAuthorId: pullRequest?.author?.account_id,
            prURL: pullRequest?.links.html.href,
            isCodeReviewer: true,
            hasCodeSuggestions: commentHasCodeSuggestions(comment),
            ...sentiment(),
          }
        : {};
    };

    return buildConversationContext({
      anchor: diffAnchor,
      destRev: diffDestRev,
      userAvatarUrl: currentUser?.links.avatar.href,
      userDisplayName: currentUser?.display_name,
      userProfileUrl: currentUser?.links.self.href,
      userUuid: currentUser?.uuid,
      fabricUser,
      urls: newUrls,
      onAddComment,
      onDeleteComment,
      owner: repositoryOwner,
      slug: repositorySlug,
      intl,
      conversationsMentionContext,
      eventHandlers,
      supportsCommentResolution: true,
      repoId,
      workspacePaid: isWorkspacePaid,
      highlightedCommentId,
      highlightCommentThread,
      baseRevHash:
        iterativeReviewSelectedDiff.selectedDiffType === SelectedDiffType.All
          ? undefined
          : iterativeReviewSelectedDiff.destHash,
      pullRequest,
      editorSource: EditorSource.PR_CONVERSATION,
      renderBetweenCommentAndActions,
      additionalAnalyticsAttributes,
    });
  }, [
    diffAnchor,
    diffDestRev,
    currentUser,
    fabricUser,
    repositoryOwner,
    pullRequestId,
    commentSave,
    commentUpdate,
    commentDelete,
    commentResolve,
    repositorySlug,
    intl,
    conversationsMentionContext,
    dispatch,
    repoId,
    isWorkspacePaid,
    highlightedCommentId,
    highlightCommentThread,
    iterativeReviewSelectedDiff.selectedDiffType,
    iterativeReviewSelectedDiff.destHash,
    pullRequest,
    onCommentCreate,
    isCodeReviewerFeedbackStatsigGateEnabled,
    commentUUIDs,
  ]);

  useEffect(() => {
    // fetch info related to pending merge status that's used by multiple
    // child components in this tabbed view (header, PR status message)
    dispatch(fetchPendingMergeStatus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API request to fetch conflicts, conflict info needed by multiple nested components
  const { isLoading: isLoadingConflicts } = useFetchConflicts(
    anchorHash,
    destHash,
    destRepoFullSlug
  );

  useEffect(() => {
    if (autoreviewDogfoodingFlagEnabled && !autoreviewDogfoodingFlagViewed) {
      showFlagComponent(AUTOREVIEW_DOGFOODING_FLAG_ID);
    }
  }, [
    showFlagComponent,
    autoreviewDogfoodingFlagEnabled,
    autoreviewDogfoodingFlagViewed,
  ]);

  return (
    <div className="prCssVarContainer" ref={cssVarContainerRef}>
      <TabsContext.Provider
        value={{
          selectedTabIndex: PULL_REQUEST_TABS.indexOf(tabSelected),
          onSelectTab,
          fileCount,
          commitCount,
        }}
      >
        <PullRequestKeyboardShortcuts switchTab={switchTabShortcutHandler}>
          <Grid layout="fluid" spacing="comfortable">
            <ConversationsContextProvider value={conversationContext}>
              <DiffFeaturesContext.Provider value={diffFeaturesContext}>
                <DiffStateContext.Provider
                  value={{
                    iterativeReviewSelectedDiff,
                    setIterativeReviewSelectedDiff,
                    activeIndex,
                    setActiveIndex,
                    multiCommitSelection,
                    setMultiCommitSelection,
                    commitCSLRHash,
                    setCommitCSLRHash,
                  }}
                >
                  <PullRequestDiffProvider>
                    <PreLoadDiffStat />
                    {/* All misc. components needed by more than 1 of the 3 tabs */}
                    <SyntaxHighlightingFlagWrapper />
                    <PullRequestDialogZone />
                    <PendingReviewWarningCheck />
                    <MetricSender metric={PULL_REQUEST_LOAD} />
                    <Header
                      cssVarContainerRef={cssVarContainerRef}
                      isLoadingConflicts={isLoadingConflicts}
                    />
                    <Box xcss={tabsWrapper}>
                      {/* PRTabsContainer includes the main Tabs component (@atlaskit/tabs) and 3 tabs in TabList.
                  The panels will be rendered as children */}
                      <PRTabsContainer isLoadingConflicts={isLoadingConflicts}>
                        <CustomTabPanel>
                          <PROverviewTab
                            isLoadingConflicts={isLoadingConflicts}
                          />
                        </CustomTabPanel>
                        <CustomTabPanel>
                          <DiffTab />
                        </CustomTabPanel>
                        <CustomTabPanel>
                          <CommitTab isLoadingConflicts={isLoadingConflicts} />
                        </CustomTabPanel>
                      </PRTabsContainer>
                    </Box>
                  </PullRequestDiffProvider>
                </DiffStateContext.Provider>
              </DiffFeaturesContext.Provider>
            </ConversationsContextProvider>
          </Grid>
        </PullRequestKeyboardShortcuts>
      </TabsContext.Provider>
    </div>
  );
};
